import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { Align, FontType } from "../../../common/TextUtils"
import { ClassStoreSA } from "../../../react/classes/ClassStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { ExpandoSprite } from "../../generic-sprites/ExpandoSprite"
import { GenericAssets, loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { AudioSync } from "../../utils/AudioSync"
import { CameraZoom, ZoomInfo } from "../../utils/CameraZoom"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { Ecig101Assets, loadAssets } from "./Ecig101Assets"
import { Ecig101Pos } from "./Ecig101Pos"


export class Ecig101State extends Phaser.State {

    // Useful for debugging. Click it quick!
    private skipButton: Phaser.Button

    private is6thGrade: boolean

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorLightGray(this.game)
        this.is6thGrade = !ClassStoreSA.instance.is5thGrade()
        this.introScene()
    }

    introScene = () => {

        const introAudio = this.game.add.audio(Ecig101Assets.INTRO_AUDIO)

        const introScene = this.game.add.image(0, 0, Ecig101Assets.INTRO_SCENE)
        Scale.scaleSprite(introScene, 0.7)
        Sprites.centerInWorld(introScene)

        const packCigarettes = this.game.add.image(Ecig101Pos.packCigarettesX(), Ecig101Pos.packCigarettesY(),
            Ecig101Assets.PACK_CIGARETTES)
        Scale.scaleSprite(packCigarettes, 0.41)

        let undoZoom: ZoomInfo
        const oneOffs = new AudioSync(this.game, introAudio)

        oneOffs.add(2500, () => {
            Sprites.fadeOut(introScene)
            undoZoom = CameraZoom.zoom(this.game, packCigarettes, 2000, Scale.width() * 0.02)
        })

        oneOffs.add(5500, () => {
            Sprites.fadeOut(packCigarettes, 250)
        })

        const match = this.game.add.sprite(Ecig101Pos.matchX(), Ecig101Pos.matchY(), Ecig101Assets.MATCH)
        Scale.scaleSprite(match, 0.9)
        const lightMatch = match.animations.add("light")
        match.alpha = 0
        const lightableCig = this.game.add.sprite(Ecig101Pos.lightableCigX(), Ecig101Pos.lightableCigY(),
            Ecig101Assets.LIGHTABLE_CIGARETTE)
        Scale.scaleSprite(lightableCig, 0.9)
        const lightCig = lightableCig.animations.add("light")
        lightableCig.alpha = 0

        oneOffs.add(6000, () => {
            CameraZoom.instaZoom(undoZoom)
            Sprites.fade(match)
            Sprites.fade(lightableCig)
        })

        oneOffs.add(7000, () => {
            lightMatch.play()
        })
        oneOffs.add(7250, () => {
            lightCig.play()
        })

        const ecigs = this.game.add.image(0, 0, Ecig101Assets.ECIGS)
        Scale.scaleSprite(ecigs, 0.7)
        Sprites.centerInWorld(ecigs)
        ecigs.alpha = 0
        const ecigsText = PhaserText.create(this.game,
            "E-cigarettes are also called pod mods, e-cigs, vape pens, and other names. We’re just going to call them " +
            "e-cigarettes throughout this program to make it simple.",
            {y: Ecig101Pos.ecigsTextY(), wordWrapWidth: 0.78})
        Sprites.centerInWorldXOnly(ecigsText)
        ecigsText.alpha = 0

        oneOffs.add(10000, () => {
            match.destroy()
            lightableCig.destroy()
            Sprites.fade(ecigs)
        })

        oneOffs.add(16000, () => {
            Sprites.fade(ecigsText)
        })

        const ecigPartsScale = 0.55
        const ecigParts = this.game.add.group()
        const vapePen = ecigParts.create(0, Ecig101Pos.vapePenY(), Ecig101Assets.VAPE_PEN)
        Scale.scaleSprite(vapePen, ecigPartsScale)
        Sprites.centerInWorldXOnly(vapePen)

        const ecigExpandoFactory = ExpandoSprite.expandoFactory(this.game, ecigPartsScale, false, ecigParts)

        const battery = ecigExpandoFactory(0, Ecig101Pos.vapePenPartsY(), Ecig101Assets.BATTERY)
        const heatingElement = ecigExpandoFactory(0, Ecig101Pos.vapePenPartsY(), Ecig101Assets.HEATING_ELEMENT)
        const cartridge = ecigExpandoFactory(
            Ecig101Pos.cartridgeX(), Ecig101Pos.vapePenPartsY() - Scale.height() * 0.006, Ecig101Assets.CARTRIDGE
        )
        const mouthPiece = ecigExpandoFactory(
            Sprites.toRightOf(cartridge, -0.263), Ecig101Pos.vapePenPartsY() + Scale.height() * 0.002, Ecig101Assets.MOUTH_PIECE
        )
        heatingElement.x = Sprites.toRightOf(cartridge, -0.086)
        battery.x = heatingElement.x + heatingElement.width - battery.width
        const mouthPieceOriginalX = mouthPiece.x
        const cartridgeOriginalX = cartridge.x
        const heatingElementOriginalX = heatingElement.x
        const batteryOriginalX = battery.x

        ecigParts.forEach((ecigPart) => {
            ecigPart.alpha = 0
        }, this)

        oneOffs.add(28000, () => {
            Sprites.fadeOut(ecigs)
            Sprites.fadeOut(ecigsText)

            ecigParts.forEach((part) => {
                Sprites.fade(part)
            }, this)
        })

        oneOffs.add(33000, () => {
            CameraZoom.moveSprite(mouthPiece, Ecig101Pos.mouthPieceX2())
            CameraZoom.moveSprite(cartridge, Ecig101Pos.cartridgeX2())
            CameraZoom.moveSprite(heatingElement, Ecig101Pos.heatingElementX2())
            CameraZoom.moveSprite(battery, Ecig101Pos.batteriesX2())
        })

        introAudio.onStop.addOnce(() => {
            ecigParts.forEach((part: ExpandoSprite) => {
                if (part !== vapePen) {
                    part.on = true
                    part.addOnClick(() => {

                        ecigParts.forEach((otherPart) => {
                            otherPart.on = false
                        })

                        const partX = part.x
                        const partY = part.y
                        const partScale = Scale.spritesCurrentScale(part)
                        const targetScale = partScale * 2.5
                        let audio: Phaser.Sound
                        const targetY = vapePen.y + vapePen.height + Scale.height() * 0.3
                        let title: string
                        if (part === mouthPiece) {
                            audio = this.game.add.sound(Ecig101Assets.MOUTH_PIECE_AUDIO)
                            title = "Mouth Piece"
                        } else if (part === cartridge) {
                            audio = this.game.add.sound(Ecig101Assets.CARTRIDGE_AUDIO)
                            title = "Cartridge"
                        } else if (part === heatingElement) {
                            audio = this.game.add.sound(Ecig101Assets.HEATING_ELEMENT_AUDIO)
                            title = "Heating Element"
                        } else if (part === battery) {
                            audio = this.game.add.sound(Ecig101Assets.BATTERY_AUDIO)
                            title = "Battery"
                        }

                        const ecigTitleText = PhaserText.create(this.game, title, {
                            y: Ecig101Pos.partTitleY(), fontPx: 36, align: Align.CENTER, type: FontType.SCIENTIFIC_TITLE
                        })
                        Sprites.centerInWorldXOnly(ecigTitleText)

                        audio.onStop.addOnce(() => {
                            part.x = partX
                            part.y = partY
                            Scale.scaleSprite(part, partScale)
                            part.grayOut()
                            ecigTitleText.destroy()
                            let allGrayed = true
                            ecigParts.forEach((otherPart) => {
                                const isOn = otherPart.tint !== colorNum.gray
                                otherPart.alpha = 1
                                otherPart.on = isOn
                                if (otherPart !== vapePen && isOn) {
                                    allGrayed = false
                                }
                            }, this)
                            if (allGrayed) {

                                vapePen.alpha = 0
                                mouthPiece.grayOut(false)
                                cartridge.grayOut(false)
                                heatingElement.grayOut(false)
                                battery.grayOut(false)
                                CameraZoom.moveSprite(mouthPiece, mouthPieceOriginalX)
                                CameraZoom.moveSprite(cartridge, cartridgeOriginalX)
                                CameraZoom.moveSprite(heatingElement, heatingElementOriginalX)
                                const moveTween = CameraZoom.moveSprite(battery, batteryOriginalX)

                                moveTween.onComplete.addOnce(() => {
                                    this.startEcigPower(vapePen)
                                    ecigParts.remove(vapePen)
                                    ecigParts.forEach((ecigPart) => {
                                        const fadeTween = Sprites.fadeOut(ecigPart)
                                        fadeTween.onComplete.addOnce(() => {
                                            ecigPart.destroy()
                                        })
                                    }, this)
                                })
                            } else {
                                part.on = true
                            }
                        })

                        Scale.scaleSprite(part, targetScale)
                        Sprites.centerInWorldXOnly(part)
                        const targetX = part.x
                        Scale.scaleSprite(part, partScale)
                        part.x = partX
                        CameraZoom.zoomSprite(part, targetX, targetY, targetScale, 500, () => {
                            audio.play()
                        })

                        ecigParts.forEach((otherPart) => {
                            if (otherPart !== part && otherPart !== vapePen) {
                                otherPart.alpha = 0
                                otherPart.on = false
                            }
                        }, this)
                    })
                }
            }, this)
        })

        this.skipButton =
            this.game.add.button(Ecig101Pos.skipButtonX(), Ecig101Pos.skipButtonY(), GenericAssets.NEXT_BUTTON,
                null, this, 1, 0)
        this.skipButton.visible = CCUtils.debug
        Scale.scaleSprite(this.skipButton, 0.9)
        this.skipButton.onInputDown.addOnce(() => {
            oneOffs.stop()
            introAudio.stop()
            introScene.destroy()
            packCigarettes.destroy()
            const skipVapePen = ecigParts.create(0, Ecig101Pos.vapePenY(), Ecig101Assets.VAPE_PEN)
            Scale.scaleSprite(skipVapePen, ecigPartsScale)
            this.startEcigPower(skipVapePen)
        })
        oneOffs.start()
    }

    startEcigPower = (vapePen: Phaser.Sprite) => {

        const guyPenVaping = this.scaleCenterInvis(Ecig101Assets.VAPE_PEN_SILHOUETTE_1, 2 / 3)
        guyPenVaping.y = Scale.height() - guyPenVaping.height
        this.game.add.existing(vapePen)

        Sprites.centerInWorld(vapePen)
        Sprites.fade(vapePen)

        // We want to delay the start of the audio by 1 second.
        let ecigPowerAudio
        if (this.is6thGrade) {
            ecigPowerAudio = this.game.add.audio(Ecig101Assets.ECIG_POWER_AUDIO_6TH)
        } else {
            ecigPowerAudio = this.game.add.audio(Ecig101Assets.ECIG_POWER_AUDIO)
        }
        const oneOffs = new AudioSync(this.game, ecigPowerAudio)
        const startAudio = this.game.time.create()

        startAudio.add(1000, () => {
            oneOffs.start()
        })

        startAudio.start()


        if (this.is6thGrade) {
            oneOffs.add(13000, () => {
                Sprites.fadeOut(vapePen)
            })
        } else {
            // Rotate the vape pen to a guy vaping it (animated)
            oneOffs.add(1000, () => {
                this.game.add.tween(vapePen).to({angle: 100}, 1000, Phaser.Easing.Linear.None, true)
                CameraZoom.zoomSprite(vapePen, Ecig101Pos.vape2X(), Ecig101Pos.vape2Y(), 0.26)
            })

            oneOffs.add(2000, () => {
                Sprites.fade(guyPenVaping)
                Sprites.fadeOut(vapePen)
            })
            oneOffs.add(5000, () => guyPenVaping.loadTexture(Ecig101Assets.VAPE_PEN_SILHOUETTE_2))
            oneOffs.add(6000, () => guyPenVaping.loadTexture(Ecig101Assets.VAPE_PEN_SILHOUETTE_3))
            oneOffs.add(7000, () => guyPenVaping.loadTexture(Ecig101Assets.VAPE_PEN_SILHOUETTE_4))

            // Vape mod with flashing button
            const vapeMod = this.scaleCenterInvis(Ecig101Assets.VAPE_MOD, 1)
            const vapeLight = vapeMod.animations.add("light")

            oneOffs.add(8000, () => {
                Sprites.fadeOut(guyPenVaping)
                Sprites.fade(vapeMod)
                vapeLight.play(1, true)
            })

            oneOffs.add(14000, () => {
                Sprites.fadeOut(vapeMod)
            })
        }

        const guyVaping = this.scaleCenterInvis(Ecig101Assets.VAPE_SILHOUETTE_1, 2 / 3)
        guyVaping.y = Scale.height() - guyVaping.height

        oneOffs.add(14000, () => {
            Sprites.fade(guyVaping)
        })

        oneOffs.add(17000, () => guyVaping.loadTexture(Ecig101Assets.VAPE_SILHOUETTE_2))
        oneOffs.add(18000, () => guyVaping.loadTexture(Ecig101Assets.VAPE_SILHOUETTE_3))
        oneOffs.add(19000, () => guyVaping.loadTexture(Ecig101Assets.VAPE_SILHOUETTE_4))
        oneOffs.add(20000, () => guyVaping.loadTexture(Ecig101Assets.VAPE_SILHOUETTE_5))

        const ecigTypes = this.scaleCenterInvis(Ecig101Assets.ECIG_TYPES, 0.6)
        ecigTypes.x -= Scale.width() * 0.22
        const eliquids = new ExpandoSprite(this.game, 0, 0, Ecig101Assets.ELIQUIDS, {scale: 0.5, on: false})
        eliquids.x = Sprites.toRightOf(ecigTypes, 0.1)
        Sprites.bottomAlign(ecigTypes, eliquids)
        eliquids.alpha = 0

        oneOffs.add(21500, () => {
            Sprites.fade(ecigTypes)
            Sprites.fadeOut(guyVaping)
            Sprites.fade(eliquids)
        })

        ecigPowerAudio.onStop.addOnce(() => {
            eliquids.on = true
            eliquids.addOnClick(() => {
                Sprites.fadeOut(ecigTypes)
                this.eliquidsInfo(eliquids)
            })
        })

        this.skipButton.onInputDown.removeAll()
        this.skipButton.onInputDown.addOnce(() => {
            oneOffs.stop()
            startAudio.stop()
            vapePen.destroy()
            Sprites.fade(eliquids)
            eliquids.on = true
            eliquids.addOnClick(() => {
                Sprites.fadeOut(ecigTypes)
                this.eliquidsInfo(eliquids)
            })
            this.eliquidsInfo(eliquids)
        })
    }

    eliquidsInfo = (eliquids: ExpandoSprite) => {
        eliquids.on = false
        eliquids.input.enabled = false
        const undoZoom = CameraZoom.zoom(this.game, eliquids, 1000, Scale.width() * 0.05)

        let eliquidAudio
        if (this.is6thGrade) {
            eliquidAudio = this.game.add.audio(Ecig101Assets.ELIQUIDS_AUDIO_6TH)
        } else {
            eliquidAudio = this.game.add.audio(Ecig101Assets.ELIQUIDS_AUDIO)
        }
        const oneOffs = new AudioSync(this.game, eliquidAudio)

        const cigPack = this.game.add.sprite(Ecig101Pos.otherCigPackX(), 0, Ecig101Assets.PACK_CIGARETTES)
        cigPack.alpha = 0
        Scale.scaleSprite(cigPack, 0.7)
        Sprites.bottomAlign(eliquids, cigPack)

        const eliquidsText = PhaserText.create(this.game, "e-liquid and e-juice = e-liquid", {
            y: Math.floor(eliquids.y + eliquids.height + Scale.height() * 0.02), fontPx: 20, align: Align.CENTER
        })
        Sprites.centerInWorldXOnly(eliquidsText)
        eliquidsText.alpha = 0
        eliquidsText.x = Math.floor((eliquids.x + eliquids.width / 2) - (eliquidsText.width * 0.5))

        oneOffs.add(3000, () => {
            Sprites.fade(eliquidsText)
        })
        oneOffs.add(9000, () => {
            Sprites.fadeOut(eliquidsText)
        })

        eliquidAudio.onStop.addOnce(() => {

            const endTimer = this.game.time.create()
            endTimer.add(1000, () => {
                Sprites.fadeOut(cigPack)
                const ecigs = this.game.add.image(Ecig101Pos.secondEcigsX(), 0, Ecig101Assets.ECIG_TYPES)
                Scale.scaleSprite(ecigs, 0.6)
                ecigs.alpha = 0
                ecigs.y = eliquids.y + eliquids.height - ecigs.height
                Sprites.fade(ecigs)

                const ecigsLabel = PhaserText.create(this.game, "E-Cigarettes", {
                    fontPx: 36, type: FontType.SCIENTIFIC_TITLE
                })
                Sprites.centerTextInSprite(ecigs, ecigsLabel)
                ecigsLabel.y = Ecig101Pos.exitLabelsY()
                ecigsLabel.visible = false
                const eliquidsLabel = PhaserText.create(this.game, "E-Liquids", {
                    fontPx: 36, type: FontType.SCIENTIFIC_TITLE
                })
                Sprites.centerTextInSprite(eliquids, eliquidsLabel)
                eliquidsLabel.y = Ecig101Pos.exitLabelsY()
                eliquidsLabel.visible = false

                const exitAudio = this.game.add.audio(Ecig101Assets.EXIT_AUDIO)

                exitAudio.onStop.addOnce(() => {
                    const audioOver = this.game.time.create()
                    audioOver.add(1000, () => {
                        Transitions.startOutro(this.game, this.is6thGrade ? ActivityKey.ECIG_201 : ActivityKey.ECIG_101)
                    })
                    audioOver.start()
                })

                const exitSync = new AudioSync(this.game, exitAudio)
                exitSync.add(10200, () => {
                    ecigsLabel.visible = true
                })
                exitSync.add(10800, () => {
                    eliquidsLabel.visible = true
                })
                exitSync.start()

                exitAudio.play()
            })
            endTimer.start()
        })

        oneOffs.add(24000, () => {
            CameraZoom.performZoom(undoZoom)
            Sprites.fade(cigPack)
        })
        oneOffs.start()
    }

    scaleCenterInvis = (key: string, scale: number): Phaser.Sprite => {
        const sprite = this.game.add.sprite(0, 0, key)
        Scale.scaleSprite(sprite, scale)
        Sprites.centerInWorld(sprite)
        sprite.alpha = 0
        return sprite
    }

}
